<script setup lang="ts">
import {
  HireType,
  JobLocationContext,
  type JobTravelExpectations,
  type V1SnapshotOutreachRadius,
} from '@factoryfixinc/ats-interfaces';
import { computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import {
  HireTypeItems,
  ShiftHours,
  ShiftItems,
} from '@/core/jobs/copilot-activation/local-job-data.type';

import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import { type WorkPerformed } from '@/core/jobs/copilot-activation/location-context.utils';
import ChevronIcon from '@/assets/svg/conversations/ChevronIcon.svg?component';
import MessageTemplate from '@/components/Shared/Input/Template/MessageTemplate.vue';
import JobDescriptionForm from '@/components/Jobs/CopilotActivation/JobDescriptionForm.vue';
import MeService from '@/core/shared/me/me.service';
import { isAValidShift, isRequired, isValidPostalCode5 } from '@/utils/forms';
import { GeolocationPersistence } from '@/core/shared/geolocation/geolocation.persistence';
import CompanyInfoForm from './CompanyInfoForm.vue';
import FormSection from './FormSection.vue';
import JobCityAutocomplete from './JobCityAutocomplete.vue';
import JobPayRangeFields from './JobPayRangeFields.vue';
import JobStateSelect from './JobStateSelect.vue';
import JobTitleStrengthMeter from './JobTitleStrengthMeter.vue';
import WorkPerformedQuestion from './WorkPerformedQuestion.vue';
import WorkerLivesQuestion from './WorkerLivesQuestion.vue';
import SourcingRadiusSelector from './SourcingRadiusSelector.vue';
import TerritoryCheckCard from './TerritoryCheckCard.vue';
import TravelExpectationsSelector from './TravelExpectationsSelector.vue';
import { useCityStatePairing } from './useCityStatePairing';

// Design copy for the lives_near radius — it is a sourcing area, not a commute.
const LIVES_NEAR_RADIUS_HELPER_TEXT =
  'Choose how far from this city candidates can live. ' +
  'This is separate from how far they travel to job sites.';
const NATIONWIDE_PREVIEW_LABEL = 'Remote · Nationwide (U.S.)';

const meService = new MeService();
const copilotActivationService = new CopilotActivationService();
const geolocationPersistence = new GeolocationPersistence();
const display = useDisplay();

defineProps<{
  isDisabled: boolean;
  isContinueLogicEnabled: boolean;
}>();

const emits = defineEmits<{
  (e: 'init:job-description-form'): void;
}>();

const usingDisplayJobTitle = ref(copilotActivationService.displayTitle);
const showJobTitleStrengthMeter = ref(false);

const isDesktop = computed(() => display.mdAndUp.value);
const companyName = computed(() => meService.employer?.name ?? '');

const locationContext = computed<JobLocationContext | null>({
  get: () => copilotActivationService.locationContext,
  set: (value) => {
    copilotActivationService.locationContext = value;
  },
});

const travelExpectations = computed<JobTravelExpectations | null>({
  get: () => copilotActivationService.travelExpectations,
  set: (value) => {
    copilotActivationService.travelExpectations = value;
  },
});

// The first question's answer, not the context: "In the field" with no second answer yet is
// a null context, which must not bring the worksite address block back.
const workPerformed = computed<WorkPerformed>({
  get: () => copilotActivationService.workPerformed,
  set: (value) => {
    copilotActivationService.workPerformed = value;
  },
});

const isWorksite = computed(() => workPerformed.value === 'worksite');
// Field-branch questions (travel frequency, where the worker lives) show as soon as "In the
// field" is chosen, before the second answer resolves the context.
const isField = computed(() => workPerformed.value === 'field');
const isLivesNear = computed(
  () => isField.value && locationContext.value === JobLocationContext.LIVES_NEAR,
);
const isNationwide = computed(
  () => isField.value && locationContext.value === JobLocationContext.NATIONWIDE,
);

const { cityInput, handleStateSelected, handleCitySelected } = useCityStatePairing(
  copilotActivationService,
  geolocationPersistence,
);

// The lives_near radius is the same sourcing radius the AI recruiter uses (one source of truth).
const sourcingRadius = computed<V1SnapshotOutreachRadius>({
  get: () => copilotActivationService.projectCopilotConfigurationV1OutreachRadiusMiles,
  set: (value) => {
    copilotActivationService.projectCopilotConfigurationV1OutreachRadiusMiles = value;
  },
});

// lives_near shows its territory on the map card instead of a text preview.
const locationPreview = computed(() => (isNationwide.value ? NATIONWIDE_PREVIEW_LABEL : ''));

const isDisplayingShiftOptions = computed(() => {
  let shouldDisplay = false;
  if (copilotActivationService.hireType) {
    shouldDisplay = [HireType.FULL_TIME, HireType.PART_TIME].includes(
      copilotActivationService.hireType,
    );
  }
  return shouldDisplay;
});

const isDisplayingShiftTimes = computed(() => copilotActivationService.shift === 0);
const originalJobTitle = computed(() => copilotActivationService.originalJobTitle ?? '');
const originalHireType = computed(() => copilotActivationService.originalHireType);
const originalShift = computed(() => copilotActivationService.originalShift);

watch(originalJobTitle, (value) => {
  copilotActivationService.displayTitle = value ?? '';
});
watch(originalHireType, (value) => {
  copilotActivationService.hireType = value ?? null;
});
watch(originalShift, (value) => {
  copilotActivationService.shift = value ?? null;
});

watch(
  [() => copilotActivationService.postalCode, () => copilotActivationService.selectedJobId],
  async ([zip]) => {
    if (!isWorksite.value) return;
    if (!zip || !/^\d{5}$/.test(zip)) return;
    if (copilotActivationService.city?.trim() && copilotActivationService.state?.trim()) return;

    const result = await geolocationPersistence.fetchCityStateFromZip(zip);
    if (!result || copilotActivationService.postalCode !== zip) return;

    if (!copilotActivationService.city?.trim()) {
      copilotActivationService.city = result.city;
    }
    if (!copilotActivationService.state?.trim()) {
      copilotActivationService.state = result.state;
    }
  },
  { immediate: true },
);

function handleDisplayTitleInput() {
  // Show the strength meter on any input (keystroke)
  showJobTitleStrengthMeter.value = true;
}

async function handleDisplayTitlePlausibleUpdate(focused: boolean) {
  if (!focused) {
    //the user stoped typing or editing the job title, check if we need to update the job title id
    if (copilotActivationService.displayTitle !== usingDisplayJobTitle.value) {
      const jobTitle = await copilotActivationService.fetchRelatedJobTitleForDisplayTitle(
        `${copilotActivationService.displayTitle ?? ''}`,
      );

      if (jobTitle) {
        copilotActivationService.jobTitleId = jobTitle.id;
      }

      usingDisplayJobTitle.value = copilotActivationService.displayTitle;
    }
  }
}

function handleJobDescriptionFormInit() {
  emits('init:job-description-form');
}
</script>

<template>
  <FormSection
    id="job-details"
    title="Describe the role"
    subtitle="Share essential job details"
    next-section-id="copilot-configuration-template"
    :is-continue-logic-enabled="isContinueLogicEnabled"
  >
    <!-- JOB TITLE -->
    <p class="mb-0.5 font-sans text-xs font-bold leading-[18px] text-shade-880">Job title*</p>
    <v-text-field
      v-model="copilotActivationService.displayTitle"
      placeholder="Job title"
      variant="outlined"
      density="compact"
      autofocus
      validate-on="submit"
      :disabled="isDisabled"
      :rules="[isRequired]"
      @input="handleDisplayTitleInput"
      @update:focused="handleDisplayTitlePlausibleUpdate"
    >
      <template #message="{ message }">
        <MessageTemplate :message="message" />
      </template>
    </v-text-field>

    <!-- JOB TITLE STRENGTH METER -->
    <JobTitleStrengthMeter
      v-if="showJobTitleStrengthMeter"
      :job-title="copilotActivationService.displayTitle ?? ''"
    />

    <!-- HIRE TYPE | SHIFT -->
    <div class="flex flex-wrap">
      <!-- HIRE TYPE -->
      <div :class="{ 'w-1/2 pr-[5px]': isDesktop, 'w-full': !isDesktop }">
        <p class="mb-0.5 font-sans text-xs font-bold leading-[18px] text-shade-880">Hire type*</p>
        <v-combobox
          v-model:model-value="copilotActivationService.hireType"
          :items="HireTypeItems"
          item-title="value"
          item-value="value"
          variant="outlined"
          density="compact"
          placeholder="Select hire type"
          :menu-icon="ChevronIcon"
          :return-object="false"
          :menu-props="{ maxWidth: '536px' }"
          :rules="[isRequired]"
          :disabled="isDisabled"
          validate-on="submit"
        >
          <template #message="{ message }">
            <MessageTemplate :message="message" />
          </template>
        </v-combobox>
      </div>
      <!-- SHIFT -->
      <div
        v-if="isDisplayingShiftOptions"
        :class="{ 'w-1/2 pl-[5px]': isDesktop, 'w-full': !isDesktop }"
      >
        <p class="mb-0.5 font-sans text-xs font-bold leading-[18px] text-shade-880">Shift*</p>
        <v-autocomplete
          v-model:model-value="copilotActivationService.shift"
          :items="ShiftItems"
          item-title="text"
          item-value="value"
          variant="outlined"
          density="compact"
          placeholder="Select a shift"
          :menu-icon="ChevronIcon"
          :return-object="false"
          :menu-props="{ maxWidth: '536px' }"
          :rules="[isAValidShift]"
          :disabled="isDisabled"
          validate-on="submit"
        >
          <template #message="{ message }">
            <MessageTemplate :message="message" />
          </template>
        </v-autocomplete>
      </div>
      <!-- SHIFT START|END TIMES -->
      <!-- @TODO: Check that time makes sense with each other -->
      <div v-if="isDisplayingShiftTimes" class="w-full">
        <p class="mb-0.5 font-sans text-xs font-bold leading-[18px] text-shade-880">
          Shift/ Job Time*
        </p>
        <div class="flex">
          <div class="w-1/2 pr-[5px]">
            <v-autocomplete
              v-model:model-value="copilotActivationService.startTime"
              :items="ShiftHours"
              item-title="value"
              item-value="value"
              variant="outlined"
              density="compact"
              placeholder="Start time"
              :menu-icon="ChevronIcon"
              :return-object="false"
              :menu-props="{ maxWidth: '536px' }"
              :rules="[isRequired]"
              :disabled="isDisabled"
              validate-on="submit"
            >
              <template #message="{ message }">
                <MessageTemplate :message="message" />
              </template>
            </v-autocomplete>
          </div>
          <div class="w-1/2 pl-[5px]">
            <v-autocomplete
              v-model:model-value="copilotActivationService.endTime"
              :items="ShiftHours"
              item-title="value"
              item-value="value"
              variant="outlined"
              density="compact"
              placeholder="End time"
              :menu-icon="ChevronIcon"
              :return-object="false"
              :menu-props="{ maxWidth: '536px' }"
              :rules="[isRequired]"
              :disabled="isDisabled"
              validate-on="submit"
            >
              <template #message="{ message }">
                <MessageTemplate :message="message" />
              </template>
            </v-autocomplete>
          </div>
        </div>
      </div>
    </div>

    <!-- PAY RANGE -->
    <p class="mb-0.5 font-sans text-xs font-bold leading-[18px] text-shade-880">Pay range*</p>
    <JobPayRangeFields :disabled="isDisabled" />

    <!-- LOCATION CONTEXT: work performed -> travel frequency -> worker lives -->
    <WorkPerformedQuestion
      v-model="locationContext"
      v-model:work-performed="workPerformed"
      :is-disabled="isDisabled"
    />
    <template v-if="isField">
      <TravelExpectationsSelector
        v-model="travelExpectations"
        :is-disabled="isDisabled"
        class="w-full"
      />
      <WorkerLivesQuestion v-model="locationContext" :is-disabled="isDisabled" />
    </template>

    <!-- ADDRESS -->
    <div class="flex flex-wrap">
      <!-- Street (worksite only) -->
      <div v-if="isWorksite" :class="{ 'w-1/2 pr-[5px]': isDesktop, 'w-full': !isDesktop }">
        <p class="mb-0.5 font-sans text-xs font-bold leading-[18px] text-shade-880">
          Street address
        </p>
        <v-text-field
          v-model="copilotActivationService.street1"
          data-test="job-street1"
          placeholder="Street address"
          variant="outlined"
          density="compact"
          validate-on="submit"
          :disabled="isDisabled"
        >
          <template #message="{ message }">
            <MessageTemplate :message="message" />
          </template>
        </v-text-field>
      </div>

      <!-- City (worksite + lives_near) -->
      <div
        v-if="isWorksite || isLivesNear"
        :class="{
          'w-1/2 pl-[5px]': isDesktop && isWorksite,
          'w-1/2 pr-[5px]': isDesktop && !isWorksite,
          'w-full': !isDesktop,
        }"
      >
        <p class="mb-0.5 font-sans text-xs font-bold leading-[18px] text-shade-880">City*</p>
        <JobCityAutocomplete
          v-if="isLivesNear"
          ref="cityInput"
          v-model="copilotActivationService.city"
          :disabled="isDisabled"
          @select="handleCitySelected"
        />
        <v-text-field
          v-else
          ref="cityInput"
          v-model="copilotActivationService.city"
          data-test="job-city"
          placeholder="City"
          variant="outlined"
          density="compact"
          validate-on="submit"
          :rules="[isRequired]"
          :disabled="isDisabled"
        >
          <template #message="{ message }">
            <MessageTemplate :message="message" />
          </template>
        </v-text-field>
      </div>

      <!-- State (worksite + lives_near) -->
      <div
        v-if="isWorksite || isLivesNear"
        :class="{
          'w-1/2 pr-[5px]': isDesktop && isWorksite,
          'w-1/2 pl-[5px]': isDesktop && !isWorksite,
          'w-full': !isDesktop,
        }"
      >
        <p class="mb-0.5 font-sans text-xs font-bold leading-[18px] text-shade-880">State*</p>
        <JobStateSelect
          :model-value="copilotActivationService.state"
          :city="copilotActivationService.city"
          suggestions-enabled
          :disabled="isDisabled"
          @update:model-value="handleStateSelected"
        />
      </div>

      <!-- Zip (worksite only) -->
      <div v-if="isWorksite" :class="{ 'w-1/2 pl-[5px]': isDesktop, 'w-full': !isDesktop }">
        <p class="mb-0.5 font-sans text-xs font-bold leading-[18px] text-shade-880">Zip*</p>
        <v-text-field
          v-model="copilotActivationService.postalCode"
          data-test="job-zip"
          placeholder="Zip"
          variant="outlined"
          density="compact"
          validate-on="submit"
          :rules="[isRequired, isValidPostalCode5]"
          :disabled="isDisabled"
        >
          <template #message="{ message }">
            <MessageTemplate :message="message" />
          </template>
        </v-text-field>
      </div>

      <!-- Sourcing radius + territory preview (lives_near) -->
      <div v-if="isLivesNear" class="mb-3 w-full">
        <SourcingRadiusSelector
          v-model="sourcingRadius"
          :is-disabled="isDisabled"
          title="Candidates can live within"
          :helper-text="LIVES_NEAR_RADIUS_HELPER_TEXT"
        />
        <TerritoryCheckCard
          class="mt-3"
          :city="copilotActivationService.city"
          :state="copilotActivationService.state"
          :radius-miles="sourcingRadius"
        />
      </div>

      <!-- Nationwide preview -->
      <div v-if="isNationwide" class="mb-3 w-full">
        <p
          data-test="location-preview"
          class="font-sans text-xs font-normal leading-[18px] text-shade-800"
        >
          Posts publicly as <span class="font-bold text-shade-900">{{ locationPreview }}</span
          >.
        </p>
      </div>

      <!-- Company Information -->
      <div class="w-full">
        <p class="mb-2 font-sans text-xs font-bold leading-[18px] text-shade-880">Company name</p>
        <CompanyInfoForm
          v-model="copilotActivationService.anonymous"
          :company-name="companyName"
          :disabled="isDisabled"
        />
      </div>
    </div>

    <!-- Job Description -->
    <JobDescriptionForm :disabled="isDisabled" @init:editor="handleJobDescriptionFormInit" />
  </FormSection>
</template>

<style lang="scss" scoped>
.v-input :deep(.v-field),
.v-input--horizontal :deep(.v-field) {
  border-radius: 6px;

  .v-autocomplete__selection,
  .v-combobox__selection {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }

  .v-field__outline {
    @apply text-tint-80;
  }
  input {
    @apply font-sans text-sm font-normal leading-[21px] text-shade-880;
  }
  input::placeholder {
    @apply text-shade-800;
    opacity: 1;
  }

  &:hover {
    .v-field__outline {
      @apply text-shade-840;
    }
  }
}

.v-input :deep(.v-field.v-field--focused),
.v-input--horizontal :deep(.v-field.v-field--focused) {
  .v-field__outline {
    @apply text-highlight-500;
  }
}

.v-input :deep(.v-field.v-field--error),
.v-input--horizontal :deep(.v-field.v-field--error) {
  .v-field__outline {
    @apply text-critical-500;
  }
}
</style>
